import { useEffect, useState } from 'react';
import { api } from '../api';
import { SyncBadge } from '../components/Sync';
import { ErrorBox, Field, Loading, Metric, PageHead, Panel, toast } from '../components/ui';
import { isDevice, requestSync, resetDevice, useEngine } from '../engine/client';
import { FACTORS } from '../drills/meta';
import { agoLabel, errorMessage, localDate, pct } from '../format';
import { useApi } from '../hooks';
import type { Aptitude, Settings } from '../types';
import { DeviceCloudPanel, PcCloudPanel } from '../cloud/CloudPanels';
import { useCloud } from '../cloud/state';

function DeviceSyncPanel() {
  const { sync } = useEngine();
  const reset = () => {
    const warn = sync.pending ? `\n아직 PC 로 못 보낸 기록 ${sync.pending}개가 사라집니다!` : '';
    if (window.confirm(`이 기기의 APEX 데이터를 지우고 PC 에서 다시 받을까요?${warn}`)) resetDevice();
  };
  return (
    <Panel title="동기화 (이 기기 ↔ PC)">
      <div className="row-gap">
        <SyncBadge />
        <button type="button" className="btn btn-sm" onClick={requestSync}>지금 동기화</button>
      </div>
      <div className="metrics-2">
        <Metric label="PC 로 보낼 기록" value={sync.pending} sub={sync.pending ? 'PC 와 연결되면 자동으로 보냅니다' : '모두 보냄'} />
        <Metric label="마지막 동기화" value={sync.lastSync ? agoLabel(sync.lastSync) : '—'} sub={sync.error ?? '이 기기에서도 모든 기능이 오프라인으로 동작'} />
      </div>
      {sync.conflicts > 0 && <p className="small muted">동시에 고친 기록 {sync.conflicts}건은 나중에 고친 쪽으로 맞췄고, 진 쪽 내용은 따로 보관했습니다.</p>}
      <p className="small muted">기록은 이 기기 안(브라우저 저장소)에 있고, PC 가 켜져 있을 때 맞춰집니다. AI 기능은 PC 와 연결될 때만 동작합니다.</p>
      <button type="button" className="btn btn-ghost btn-sm" onClick={reset}>이 기기 데이터 지우고 PC 에서 다시 받기</button>
    </Panel>
  );
}

export function SettingsPage() {
  const cloud = useCloud();
  const loaded = useApi(api.settings);
  const backups = useApi(api.backups);
  const [form, setForm] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (loaded.data) setForm(loaded.data);
  }, [loaded.data]);

  if (loaded.error) return <ErrorBox message={loaded.error} onRetry={loaded.reload} />;
  if (!form) return <Loading />;
  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => setForm({ ...form, [key]: value });
  const apt: Aptitude = form.aptitude ?? { date: '', scores: {}, percentiles: {} };
  const setApt = (part: 'scores' | 'percentiles', factor: string, raw: string) => {
    const next = { ...apt[part] };
    if (raw.trim() === '') delete next[factor];
    else next[factor] = Number(raw);
    const updated: Aptitude = { ...apt, date: apt.date || localDate(), [part]: next };
    const empty = !Object.keys(updated.scores).length && !Object.keys(updated.percentiles).length;
    set('aptitude', empty ? null : updated);
  };

  const save = async () => {
    try {
      setForm(await api.saveSettings(form));
      toast('설정을 저장했습니다');
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };
  const backupNow = async () => {
    try {
      const b = await api.backup();
      toast(`백업했습니다: ${b.name}`);
      backups.reload();
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };
  const restore = async (file: File) => {
    let json: unknown;
    try {
      json = JSON.parse(await file.text());
    } catch {
      toast('JSON 파일을 읽을 수 없습니다', 'err');
      return;
    }
    if (!window.confirm('지금 데이터를 모두 이 파일 내용으로 바꿉니다.\n바로 전에 자동 백업을 만듭니다. 계속할까요?')) return;
    setBusy(true);
    try {
      const r = await api.importJson(json);
      toast(`복원했습니다 (직전 백업: ${r.backup})`);
      backups.reload();
    } catch (e) {
      toast(errorMessage(e), 'err');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead
        title="설정 · 데이터"
        sub={isDevice ? '이 기기에 기록을 두고 인터넷 없이 동작합니다. PC 와 연결되면 맞춥니다.' : '모든 기록은 이 컴퓨터의 apex\\data 폴더에 저장됩니다. 인터넷 없이 동작합니다.'}
      />
      <div className="dash">
        <Panel title="복습 설정" className="span-2">
          <Field
            label={`목표 기억 유지율: ${pct(form.desired_retention)}`}
            htmlFor="s-ret"
            hint="높일수록 더 자주 복습합니다 (90% 권장, 95%는 복습량이 크게 늘어남). 바꾸면 다음 복습부터 적용됩니다."
          >
            <input id="s-ret" type="range" min={80} max={97} value={Math.round(form.desired_retention * 100)} onChange={(e) => set('desired_retention', Number(e.target.value) / 100)} />
          </Field>
          <div className="grid-2">
            <Field label="하루 새 카드 수" htmlFor="s-new" hint="이미 만든 카드가 한꺼번에 몰리지 않게">
              <input id="s-new" className="input narrow" type="number" min={0} max={200} value={form.new_per_day} onChange={(e) => set('new_per_day', Number(e.target.value))} />
            </Field>
            <Field label="하루 시작 시각" htmlFor="s-hour" hint="이 시각 전까지는 전날로 칩니다">
              <select id="s-hour" className="input narrow" value={form.day_start_hour} onChange={(e) => set('day_start_hour', Number(e.target.value))}>
                {Array.from({ length: 13 }, (_, h) => <option key={h} value={h}>{`${h}시`}</option>)}
              </select>
            </Field>
            <Field label="주간 예측 목표 (건)" htmlFor="s-pred">
              <input id="s-pred" className="input narrow" type="number" min={0} max={50} value={form.weekly_prediction_target} onChange={(e) => set('weekly_prediction_target', Number(e.target.value))} />
            </Field>
            <Field label="주간 새 개념 목표 (개)" htmlFor="s-items">
              <input id="s-items" className="input narrow" type="number" min={0} max={100} value={form.weekly_item_target} onChange={(e) => set('weekly_item_target', Number(e.target.value))} />
            </Field>
          </div>
          <label className="check">
            <input type="checkbox" checked={form.first_review_next_day} onChange={(e) => set('first_review_next_day', e.target.checked)} />
            새 카드의 첫 복습은 다음 날부터 (권장 — 만든 직후의 회상은 너무 쉬워서 효과가 작습니다)
          </label>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={() => void save()}>설정 저장</button>
          </div>
        </Panel>

        <Panel title="두뇌 훈련" className="span-2">
          <Field label="오늘 훈련 종목 수" htmlFor="s-drills" hint="오래 안 한 종목과 약한 요인을 먼저 고릅니다. 다른 종목은 훈련 화면의 '모든 종목'에서 언제든 풀 수 있습니다.">
            <select id="s-drills" className="input narrow" value={form.drills_per_day} onChange={(e) => set('drills_per_day', Number(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}개</option>)}
            </select>
          </Field>
          <h3 className="sub-title">고용24 직업적성검사 결과</h3>
          <p className="small muted">결과표의 변환점수(100 = 평균)와 백분위를 넣으면, 백분위가 낮은 요인의 종목이 오늘 훈련에 더 자주 나옵니다. 몇 달 뒤 다시 검사하면 고쳐 넣어 비교하세요 (같은 검사를 다시 보면 익숙해진 만큼 점수가 오르는 점은 감안).</p>
          <Field label="검사일" htmlFor="s-apt-date">
            <input id="s-apt-date" type="date" className="input narrow" value={apt.date} onChange={(e) => set('aptitude', { ...apt, date: e.target.value })} />
          </Field>
          <table className="table apt-table">
            <thead><tr><th>적성 요인</th><th>점수</th><th>백분위</th></tr></thead>
            <tbody>
              {FACTORS.filter((f) => f.key !== 'other').map((f) => (
                <tr key={f.key}>
                  <td>{f.label}<span className="muted small"> {f.test}</span></td>
                  <td><input className="input tiny" type="number" inputMode="numeric" min={20} max={180} aria-label={`${f.label} 점수`} value={apt.scores[f.key] ?? ''} onChange={(e) => setApt('scores', f.key, e.target.value)} /></td>
                  <td><input className="input tiny" type="number" inputMode="numeric" min={0} max={100} aria-label={`${f.label} 백분위`} value={apt.percentiles[f.key] ?? ''} onChange={(e) => setApt('percentiles', f.key, e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={() => void save()}>설정 저장</button>
          </div>
        </Panel>

        {!isDevice && <PcCloudPanel />}
        {isDevice && cloud && <DeviceCloudPanel />}

        {!(isDevice && cloud) && (
        <Panel title="내보내기">
          {isDevice && <p className="small muted">PC 서버에 있는 정본 데이터를 받습니다 (PC 와 연결돼 있어야 함).</p>}
          <ul className="list">
            <li className="list-row">
              <span className="grow"><b>전체 백업 (JSON)</b><span className="muted small">모든 표 그대로 — 복원에 씁니다</span></span>
              <a className="btn btn-sm" href="/api/export/json" download>받기</a>
            </li>
            <li className="list-row">
              <span className="grow"><b>Obsidian (Markdown zip)</b><span className="muted small">개념마다 .md + [[관련 개념]] 링크. 볼트 폴더에 풀면 됩니다</span></span>
              <a className="btn btn-sm" href="/api/export/obsidian" download>받기</a>
            </li>
            <li className="list-row">
              <span className="grow"><b>Anki (텍스트)</b><span className="muted small">Anki → 파일 → 가져오기. 탭 구분, 태그 포함</span></span>
              <a className="btn btn-sm" href="/api/export/anki" download>받기</a>
            </li>
          </ul>
        </Panel>

        )}

        {isDevice ? (
          !cloud && <DeviceSyncPanel />
        ) : (
        <Panel title="백업 · 복원">
          <div className="row-gap">
            <button type="button" className="btn" onClick={() => void backupNow()}>지금 백업</button>
            <label className={`btn ${busy ? 'disabled' : ''}`}>
              JSON에서 복원
              <input
                type="file" accept="application/json,.json" hidden disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void restore(f);
                }}
              />
            </label>
          </div>
          <p className="small muted">매일 처음 켤 때 자동 백업(30개 보관) · 위치 apex\data\backups</p>
          {backups.data && backups.data.length > 0 && (
            <ul className="list compact">
              {backups.data.slice(0, 6).map((b) => (
                <li key={b.name} className="list-row">
                  <span className="grow small">{b.name}</span>
                  <span className="muted small">{Math.max(1, Math.round(b.size / 1024))} KB</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        )}

        <Panel title="이 시스템의 원칙">
          <ul className="principles small">
            <li>AI 없이 모든 핵심 기능이 동작합니다. 로컬 AI(8단계)는 '내가 먼저 답한 뒤'에만 씁니다.</li>
            <li>IQ 같은 합산 점수는 만들지 않습니다. 능력마다 따로, 표본 수와 함께 봅니다.</li>
            <li>복습 기록은 지우지 않고 쌓습니다. 예측 확률은 수정 이력으로만 바뀝니다.</li>
            <li>근거가 약한 '두뇌 훈련 게임'은 넣지 않습니다.</li>
          </ul>
        </Panel>
      </div>
    </>
  );
}
